import { useState } from 'react';
import { Alert, Badge, Switch, useToast } from '@dreamtree-org/twreact-ui';
import { PageHeader, SectionCard, QueryState } from '@/components';
import { useQuery, useMutation } from '@/lib/useQuery';
import { errorText } from '@/lib/format';
import { usePlatformCan } from '../lib/access';

/**
 * Marketplace toggles (MERCH-89, D33): which industries and product types the marketplace shows. Off hides them from
 * the marketplace projection only; every store's own storefront keeps selling them. Each switch is audited
 * (marketplace.visibility); hidden rows leave the marketplace at once, shown ones return on the next sweep.
 */
export default function Marketplace() {
  const canManage = usePlatformCan('marketplace.manage');
  const { toast } = useToast();
  const query = useQuery('platformMarketplace', undefined, { select: (r) => r.data });
  const save = useMutation('platformMarketplaceSet');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  const flip = async (kind, key, visible) => {
    setBusy(`${kind}:${key}`);
    setErr('');
    try {
      const r = await save.run({ kind, key, visible });
      query.setData(() => r.data);
      toast.success(visible ? 'Shown in the marketplace' : 'Hidden from the marketplace', visible ? 'Listings return with the next catalog sweep.' : 'Stores keep selling it on their own storefronts.');
    } catch (ex) {
      setErr(errorText(ex));
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Marketplace" subtitle="Which industries and product types the marketplace shows. Stores' own storefronts are never affected." />
      {err ? <Alert variant="error" dismissible onDismiss={() => setErr('')}>{err}</Alert> : null}
      <QueryState query={query}>
        {(industries) => (
          <div className="grid gap-4 lg:grid-cols-2">
            {industries.map((i) => (
              <SectionCard
                key={i.key}
                title={i.label}
                actions={(
                  <Switch
                    checked={i.visible}
                    disabled={!canManage || Boolean(busy)}
                    rightLabel={i.visible ? 'Shown' : 'Hidden'}
                    onChange={(on) => flip('industry', i.key, Boolean(on))}
                  />
                )}
              >
                <ul className="divide-y divide-[var(--app-border)]">
                  {i.types.map((t) => (
                    <li key={t.key} className="flex min-h-[44px] items-center justify-between gap-2 py-2">
                      <span className={t.shown ? '' : 'opacity-60'}>
                        {t.plural}
                        {!i.visible && t.visible ? <Badge variant="secondary" size="sm" className="ml-2">Hidden with the industry</Badge> : null}
                      </span>
                      <Switch
                        checked={t.visible}
                        disabled={!canManage || Boolean(busy)}
                        rightLabel={t.visible ? 'Shown' : 'Hidden'}
                        onChange={(on) => flip('type', t.key, Boolean(on))}
                      />
                    </li>
                  ))}
                </ul>
                {i.updated_by ? <p className="mt-2 text-xs opacity-60">Hidden by {i.updated_by}</p> : null}
              </SectionCard>
            ))}
          </div>
        )}
      </QueryState>
    </div>
  );
}
